/**
 * The backend answers a refused request with a reason and a traceId. Collapsing both into a fixed
 * string ("Failed to save draft", "Something went wrong") left merchants and QA with nothing to act
 * on and no way to find the request in the logs: the booking-slot rejection, the brand-grant write
 * failure and the signed-IO check all looked like the same flaky save (MIC-3862, MIC-3878).
 *
 * Reasons are only shown for statuses that mean "your request was wrong". A 5xx is our problem, and
 * its internals are not the merchant's to read, so those keep the caller's own wording.
 */

const CLIENT_FAULT_STATUSES = [400, 409, 422];

interface ServerErrorBody {
  message?: string | string[];
  traceId?: string;
}

const bodyOf = (error: unknown): { status?: number; body?: ServerErrorBody } => {
  const response = (error as { response?: { status?: number; data?: ServerErrorBody } })?.response;
  return { status: response?.status, body: response?.data };
};

/** The server's own reason, when it gave one we should repeat. Empty string otherwise. */
export const serverReason = (error: unknown): string => {
  const { status, body } = bodyOf(error);
  if (!status || !CLIENT_FAULT_STATUSES.includes(status)) return '';
  const raw = body?.message;
  // A Nest-style upstream sends `message` as a list of validation failures.
  const text = Array.isArray(raw) ? raw.filter(Boolean).join('; ') : raw;
  return typeof text === 'string' ? text.trim() : '';
};

/** The traceId, for the cases where support needs to find the request. */
export const traceIdOf = (error: unknown): string => bodyOf(error).body?.traceId ?? '';

/**
 * Message to show: the server's reason when it gave one, otherwise the caller's fallback with the
 * traceId appended so the request can still be found.
 */
export const errorMessage = (error: unknown, fallback: string): string => {
  const reason = serverReason(error);
  if (reason) return reason;
  const traceId = traceIdOf(error);
  return traceId ? `${fallback} (ref ${traceId})` : fallback;
};
